import { useAtom, useAtomValue } from 'jotai'
import {
  chatMessagesAtom,
  conversationIdAtom,
  IConversation,
  pollConversationIdsAtom,
  selectMessageAtom,
} from '../deepseek/atom'
import { updateMessages } from '~/hooks/ai/useDeepSeekMotion'
import dayjs from 'dayjs'
import { useMutation } from '@apollo/client'
import {
  ChatMessageStatusEnum,
  ChatRole,
  ConversationType,
  pollChatMessage,
  pollChatMessageVariables,
  sendMessage,
  sendMessageVariables,
  terminateMessage,
  terminateMessageVariables,
} from '~/graphqls/types'
import {
  POLL_CHAT_MESSAGE,
  SEND_MESSAGE,
  TERMINATE_MESSAGE,
} from '~/graphqls/gqls/gpt'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'

export function useGPT4o() {
  const [, setMessages] = useAtom(chatMessagesAtom)
  const selectMessage = useAtomValue(selectMessageAtom)
  const [conversationId, setConversationId] = useAtom(conversationIdAtom)
  const [pollConversationIds, setPollConversationIds] = useAtom(
    pollConversationIdsAtom
  )
  const [sendAction] = useMutation<sendMessage, sendMessageVariables>(
    SEND_MESSAGE
  )
  const pollAction = useImperativeQuery<
    pollChatMessage,
    pollChatMessageVariables
  >(POLL_CHAT_MESSAGE)
  const [terminateAction] = useMutation<
    terminateMessage,
    terminateMessageVariables
  >(TERMINATE_MESSAGE)

  const sendMessage = async ({
    input,
    referImages,
  }: {
    input: string
    referImages?: string[]
  }) => {
    if (!input.trim()) return
    const userMessage = {
      role: ChatRole.USER,
      content: input,
      referImages: referImages,
      id: dayjs().valueOf().toString(),
      parentId: 0,
    }
    let id = conversationId
    setMessages(prev => {
      if (id) {
        return updateMessages({
          chatMessages: prev,
          conversationId: id,
          messageIndex: 0,
          action: 'add',
          newMessage: userMessage,
        }) as Record<any, IConversation>
      }

      return prev
    })
    const assistantMessage = selectMessage?.filter(
      it => it.role === ChatRole.ASSISTANT && !!it.parentId
    )

    try {
      const res = await sendAction({
        variables: {
          input: {
            conversationType: ConversationType.GPT,
            prompt: input,
            referImages: referImages || [],
            conversationId: id,
            parentId: !!assistantMessage?.length
              ? assistantMessage[assistantMessage?.length - 1].parentId
              : 0,
          },
        },
      })
      const conversationRes = res.data?.openAiMutation?.conversation
      setConversationId(conversationRes?.conversationId)

      if (!id) {
        id = conversationRes?.conversationId
        setMessages(prev => {
          return updateMessages({
            chatMessages: prev,
            conversationId: id,
            messageIndex: 0,
            action: 'add',
            newMessage: {
              conversationId: id!,
              parentId: 0,
              role: ChatRole.USER,
              content: input,
              id: dayjs().valueOf().toString(),
              referImages: referImages,
            },
          }) as Record<any, IConversation>
        })
      }
      setMessages(prev => {
        return updateMessages({
          chatMessages: prev,
          conversationId: id,
          messageIndex: 0,
          action: 'add',
          newMessage: {
            id: conversationRes?.id,
            conversationId: id!,
            parentId: conversationRes?.id,
            status: conversationRes?.status!,
            image: conversationRes?.content?.image || undefined,
            role: ChatRole.ASSISTANT,
          },
        }) as Record<any, IConversation>
      })

      setPollConversationIds(prev => [...prev, conversationRes?.id])
    } catch (err) {
      console.error(err)
      // renderError(graphQLErrorMessage(err))
      throw err
    }
  }

  const pollMessageStatus = async () => {
    if (!pollConversationIds.length) {
      return
    }
    try {
      const res = await pollAction({
        messageIdList: pollConversationIds,
      })

      res.data?.openAiQuery?.chatMessagesPolling?.map(msg => {
        if (!msg) return
        if (
          [
            ChatMessageStatusEnum.FINISH,
            ChatMessageStatusEnum.ERROR,
            ChatMessageStatusEnum.CANCEL,
          ].includes(msg.status as any)
        ) {
          if (pollConversationIds.includes(msg.id)) {
            setPollConversationIds(prev => prev.filter(id => id !== msg.id))

            setMessages(prev => {
              return updateMessages({
                chatMessages: prev,
                conversationId: msg.conversationId,
                messageIndex: prev?.[msg.conversationId]?.messages?.findIndex(
                  it => it?.id === msg?.id
                )!,
                action: 'update',
                newMessage: {
                  image: msg.content?.image || undefined,
                  status: msg.status || undefined,
                },
              }) as Record<any, IConversation>
            })
          }
        }
      })
    } catch (err) {
      console.error(err)
    } finally {
    }
  }

  const stopMessage = async () => {
    if (!conversationId) return
    const msg = selectMessage[selectMessage.length - 1]
    try {
      setPollConversationIds(prev => prev.filter(id => id !== msg.id))
      await terminateAction({
        variables: {
          messageId: msg.id,
        },
      })
      // 取消时，不管消息是任何状态，直接变为cancel
      setMessages(prev => {
        return updateMessages({
          chatMessages: prev,
          conversationId: msg.conversationId,
          messageIndex: prev?.[msg.conversationId!]?.messages?.findIndex(
            it => it?.id === msg?.id
          )!,
          action: 'update',
          newMessage: {
            status: ChatMessageStatusEnum.CANCEL,
          },
        }) as Record<any, IConversation>
      })
    } catch (e) {
      console.error(e, 'stopMessage')
    }
  }

  return {
    /** 消息列表 */
    messages: selectMessage,
    /** 发送消息函数 */
    sendMessage,
    /** 停止当前消息 */
    stopMessage,
    pollMessageStatus,
  }
}
